import { IMappedAsset } from "@sundae/wallet-react";
import { KnownCip30WalletId } from "@sundae/wallets";
import { AssetAmount } from "@sundaeswap/asset";
import { BigIntMath } from "@sundaeswap/bigint-math";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import { Fraction } from "@sundaeswap/fraction";
import { ADA_METADATA } from "../constants/cardano.constants";
import { IAssetMetaData } from "../types/Asset.types";

/**
 * The referral fee is defined as following:
 * - The fee is added to transactions worth at least 100 ADA.
 * - The fee is minimum 1 ADA.
 * - The fee is at most 1% of the taxable amount.
 */
export const MIN_TAXABLE_AMOUNT = new AssetAmount(100000000, ADA_METADATA); // 100 ADA
export const MIN_TAX_AMOUNT = new AssetAmount(1000000, ADA_METADATA); // 1 ADA
export const TAX_RATE = new Fraction(1, 1000); // 0.1%

/**
 * Gets the Eternl fee address.
 * @returns {string | undefined} The Eternl fee address.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export const getEternlFeeAddress = (): string | undefined =>
  (window as any).cardano?.[KnownCip30WalletId.Eternl]?.experimental?.feeAddress;

/**
 * This function is used to calculate the Eternl fee amount based on the assets given and taken in a transaction.
 *
 * Fee calculation is based on the following rules:
 * 1. If neither the given nor taken assets are ADA, the fee is 1 ADA.
 * 2. If the transaction amount (in ADA) is less than the minimum taxable amount, the function returns undefined (no fee).
 * 3. Otherwise, the fee is calculated as 0.1% of the taxable amount (in ADA), with a minimum of 1 ADA.
 *
 * Note: The taxable amount is the quantity of ADA involved in the transaction, which is obtained from either the 'given' or 'taken' asset.
 *
 * @param {Object} args - An object that holds the assets given and taken in a transaction.
 * @param {IMappedAsset<IAssetMetaData & IAsset>} args.given - The asset that the user is providing in the transaction.
 * @param {IMappedAsset<IAssetMetaData & IAsset>} args.taken - The asset that the user is receiving in the transaction.
 *
 * @returns {AssetAmount | undefined} - Returns the calculated fee as an AssetAmount object if the taxable amount is above the minimum taxable amount. If the taxable amount is below the minimum taxable amount, it returns undefined.
 */
export const getEternlFeeAmount = ({
  given,
  taken,
}: {
  given?: IMappedAsset<IAssetMetaData>;
  taken?: IMappedAsset<IAssetMetaData>;
}) => {
  const isGivenAda = given?.metadata ? SundaeUtils.isAdaAsset(given.metadata) : false;
  const isTakenAda = taken?.metadata ? SundaeUtils.isAdaAsset(taken.metadata) : false;
  const lovelaceValue = isGivenAda ? given?.quantity : isTakenAda ? taken?.quantity : null;

  // exotic pair; return 1 ADA
  if (!lovelaceValue) return MIN_TAX_AMOUNT;

  // non taxable amount
  if (lovelaceValue.amount < MIN_TAXABLE_AMOUNT.amount) return;

  // Return 0.1% of the taxable amount, but at least 1 ADA
  return new AssetAmount(
    BigIntMath.max(
      new Fraction(lovelaceValue.amount).multiply(TAX_RATE).quotient,
      MIN_TAX_AMOUNT.amount
    ),
    ADA_METADATA
  );
};
